/**
 * Canonical Brazilian states and municipalities from the IBGE localidades API.
 * Municipality names come properly cased and accented ("Guapiaçu"), which is the
 * standard we store. Cached in memory and sessionStorage per UF.
 */
import axios from 'axios'

const IBGE_BASE = 'https://servicodados.ibge.gov.br/api/v1/localidades'
const CACHE_PREFIX = 'acig.ibge.municipios.'

export const UFS = [
  { value: 'AC', label: 'Acre' }, { value: 'AL', label: 'Alagoas' }, { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' }, { value: 'BA', label: 'Bahia' }, { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' }, { value: 'ES', label: 'Espírito Santo' }, { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' }, { value: 'MT', label: 'Mato Grosso' }, { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' }, { value: 'PA', label: 'Pará' }, { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' }, { value: 'PE', label: 'Pernambuco' }, { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' }, { value: 'RN', label: 'Rio Grande do Norte' }, { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' }, { value: 'RR', label: 'Roraima' }, { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' }, { value: 'SE', label: 'Sergipe' }, { value: 'TO', label: 'Tocantins' },
]

const UF_SET = new Set(UFS.map((u) => u.value))
export const isValidUF = (uf) => UF_SET.has((uf || '').toUpperCase())

/** Accent/case/whitespace-insensitive key for matching city names. */
export const cityKey = (name) =>
  (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()

const memory = new Map()

export const getMunicipios = async (uf) => {
  const key = (uf || '').toUpperCase()
  if (!isValidUF(key)) return []
  if (memory.has(key)) return memory.get(key)

  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + key)
    if (cached) {
      const list = JSON.parse(cached)
      memory.set(key, list)
      return list
    }
  } catch { /* storage unavailable */ }

  const { data } = await axios.get(`${IBGE_BASE}/estados/${key}/municipios`, { params: { orderBy: 'nome' } })
  const list = (data || []).map((m) => ({ id: m.id, nome: m.nome }))
  memory.set(key, list)
  try { sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(list)) } catch { /* ignore */ }
  return list
}

/**
 * Returns the canonical IBGE name for `name` within `uf`, or null when there is
 * no match. "GUAPIACU" / "guapiaçu " / "Guapiaçu" all resolve to "Guapiaçu".
 */
export const canonicalCity = async (name, uf) => {
  if (!name) return null
  const list = await getMunicipios(uf)
  const k = cityKey(name)
  const hit = list.find((m) => cityKey(m.nome) === k)
  return hit ? hit.nome : null
}
